
import React, { useState, useEffect } from 'react';
import { database, auth } from '../firebase';
import { ref, push, serverTimestamp, update } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import PropTypes from 'prop-types';

const SupplierForm = ({ supplier, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState(supplier || {
    visibility: true,
    supplierName: '',
    under: '',
    underDetails: '',
    openingBalance: '',
    dueDays: '',
    creditLimit: '',
    dlNo: '',
    dlValidUpto: '',
    fssaiNo: '',
    fssaiValidUpto: '',
    contactPerson: '',
    address: '',
    address2: '',
    address3: '',
    city: '',
    zipCode: '',
    state: 'Maharashtra',
    country: 'India',
    phoneNo: '',
    mobileNo: '',
    faxNo: '',
    email: '',
    enableLoyalty: false,
    cardType: '',
    cardNumber: '',
    loyaltyPoints: '',
    redeemPoints: '',
    cstNo: '',
    gstTinNo: '',
    panNo: '',
    printName: '',
    obAmount: '',
    advanceAmount: '',
    creditAmount: '',
    debit: '',
    balanceAmount: '',
    smsAlert: 'NO'
  });

  const [currentUser, setCurrentUser] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  const fallbackCountry = [{ name: 'India', code: 'IN' }];
  const fallbackStates = [
    { name: 'Tamil Nadu' },
    { name: 'Andhra Pradesh' },
    { name: 'Karnataka' },
    { name: 'Kerala' },
    { name: 'Maharashtra' }
  ];

  // Validation functions
  const validateSupplierName = (value) => {
    const regex = /^[a-zA-Z\s\-]+$/;
    return regex.test(value);
  };

  const validateDLNumber = (value) => {
    const regex = /^[a-zA-Z0-9\s]+$/;
    return regex.test(value);
  };

  const validateFSSAI = (value) => {
    const regex = /^\d{0,14}$/;
    return regex.test(value);
  };

  const validateContactPersonCityAddress = (value) => {
    const regex = /^[a-zA-Z\s]+$/;
    return regex.test(value);
  };

  const validateZipCode = (value) => {
    const regex = /^\d{0,6}$/;
    return regex.test(value);
  };

  const validatePhoneNumber = (value) => {
    const regex = /^\d{0,10}$/;
    return regex.test(value);
  };

  const validateEmail = (value) => {
    if (!value) return true; // Email is optional
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(value);
  };

  const validateFaxNumber = (value) => {
    const regex = /^[a-zA-Z0-9\s!@#$%^&*()_+\-=\[\]{}\\|;:'",.<>?/`~]*$/;
    return regex.test(value);
  };

  const validateCSTNo = (value) => {
    const regex = /^[a-zA-Z0-9\s!@#$%^&*()_+\-=\[\]{}\\|;:'",.<>?/`~]*$/;
    return regex.test(value);
  };

  const validateGSTTIN = (value) => {
    const regex = /^[a-zA-Z0-9]{0,15}$/;
    return regex.test(value);
  };

  const validatePAN = (value) => {
    const regex = /^[a-zA-Z0-9]{0,10}$/;
    return regex.test(value);
  };

  const validatePrintName = (value) => {
    const regex = /^[a-zA-Z\s]+$/;
    return regex.test(value);
  };

  useEffect(() => {
    const fetchCountries = async () => {
      setLoading(true);
      try {
        const response = await fetch('https://restcountries.com/v3.1/name/india');
        if (!response.ok) throw new Error('Failed to fetch countries');
        const data = await response.json();
        const indiaCountry = data
          .filter(country => country.name.common === 'India')
          .map(country => ({
            name: country.name.common,
            code: country.cca2
          }));
        setCountries(indiaCountry.length > 0 ? indiaCountry : fallbackCountry);
      } catch (error) {
        console.error('Error fetching countries:', error);
        setErrorMessage('Failed to load country data. Using fallback data.');
        setCountries(fallbackCountry);
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchStates = async () => {
      if (!formData.country) {
        setStates([]);
        return;
      }
      setLoading(true);
      try {
        const response = await fetch('https://countriesnow.space/api/v0.1/countries/states', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ country: formData.country })
        });
        if (!response.ok) throw new Error('Failed to fetch states');
        const data = await response.json();
        if (data.error) throw new Error(data.msg);
        setStates(data.data.states || fallbackStates);
      } catch (error) {
        console.error('Error fetching states:', error);
        setErrorMessage('Failed to load states. Using fallback data.');
        setStates(fallbackStates);
      } finally {
        setLoading(false);
      }
    };

    fetchStates();
  }, [formData.country]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Auto-calculate balanceAmount when creditAmount or debit changes
    const creditAmount = Number(formData.creditAmount) || 0;
    const debit = Number(formData.debit) || 0;
    setFormData(prev => ({
      ...prev,
      balanceAmount: creditAmount - debit
    }));
  }, [formData.creditAmount, formData.debit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const numericFields = [
      'openingBalance',
      'dueDays',
      'creditLimit',
      'loyaltyPoints',
      'redeemPoints',
      'obAmount',
      'advanceAmount',
      'creditAmount',
      'debit',
      'balanceAmount'
    ];
  
    let newValue = value;
    let isValid = true;
  
    // Validation logic
    if (name === 'supplierName' && value && !validateSupplierName(value)) {
      setErrorMessage('Supplier Name can only contain letters, spaces, and hyphens.');
      isValid = false;
    } else if (name === 'dlNo' && value && !validateDLNumber(value)) {
      setErrorMessage('DL Number can only contain letters, numbers, and spaces.');
      isValid = false;
    } else if (name === 'fssaiNo' && value && !validateFSSAI(value)) {
      setErrorMessage('FSSAI License Number must contain only digits (up to 14).');
      isValid = false;
    } else if ((name === 'contactPerson' || name === 'city' || name === 'address' || name === 'address2' || name === 'address3') && value && !validateContactPersonCityAddress(value)) {
      setErrorMessage(`${name.replace(/([A-Z])/g, ' $1').trim()} can only contain letters and spaces.`);
      isValid = false;
    } else if (name === 'zipCode' && value && !validateZipCode(value)) {
      setErrorMessage('ZIP Code must contain only digits (up to 6).');
      isValid = false;
    } else if ((name === 'mobileNo' || name === 'phoneNo') && value && !validatePhoneNumber(value)) {
      setErrorMessage(`${name.replace(/([A-Z])/g, ' $1').trim()} must contain only digits (up to 10).`);
      isValid = false;
    } else if (name === 'email' && value && !validateEmail(value)) {
      setErrorMessage('Please enter a valid email address.');
      isValid = false;
    } else if (name === 'faxNo' && value && !validateFaxNumber(value)) {
      setErrorMessage('Fax Number can contain letters, numbers, and special symbols.');
      isValid = false;
    } else if (name === 'cstNo' && value && !validateCSTNo(value)) {
      setErrorMessage('CST No can contain letters, numbers, and special symbols.');
      isValid = false;
    } else if (name === 'gstTinNo' && value && !validateGSTTIN(value)) {
      setErrorMessage('GST TIN No can only contain letters and numbers (up to 15 characters).');
      isValid = false;
    } else if (name === 'panNo' && value && !validatePAN(value)) {
      setErrorMessage('PAN No can only contain letters and numbers (up to 10 characters).');
      isValid = false;
    } else if (name === 'printName' && value && !validatePrintName(value)) {
      setErrorMessage('Print Name can only contain letters and spaces.');
      isValid = false;
    } else if (numericFields.includes(name)) {
      if (value !== '' && isNaN(value)) {
        setErrorMessage(`${name.replace(/([A-Z])/g, ' $1').trim()} must be a number.`);
        isValid = false;
      } else {
        newValue = value === '' ? '' : Number(value);
      }
    }
  
    if (isValid || value === '') {
      setFormData(prev => {
        let updatedFormData = {
          ...prev,
          [name]: type === 'checkbox' ? checked : newValue
        };
  
        if (name === 'openingBalance') {
          updatedFormData.obAmount = newValue;
        }
  
        if (name === 'creditLimit') {
          updatedFormData.creditAmount = newValue;
        }
  
        return updatedFormData;
      });
      
      if (isValid) {
        setErrorMessage('');
      }
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');

    if (!currentUser) {
      setErrorMessage('You must be logged in to modify supplier data.');
      return;
    }

    const requiredFields = [
      { field: 'supplierName', label: 'Supplier Name' },
      { field: 'under', label: 'Under' },
      { field: 'openingBalance', label: 'Opening Balance' },
      { field: 'dlNo', label: 'DL No' },
      { field: 'dlValidUpto', label: 'DL Valid Upto' },
      { field: 'fssaiNo', label: 'FSSAI No' },
      { field: 'fssaiValidUpto', label: 'FSSAI Valid Upto' }
    ];

    if (formData.enableLoyalty) {
      requiredFields.push(
        { field: 'cardType', label: 'Card Type' },
        { field: 'cardNumber', label: 'Card Number' },
        { field: 'loyaltyPoints', label: 'Loyalty Points' },
        { field: 'redeemPoints', label: 'Redeem Points' }
      );
    }

    const missingFields = requiredFields.filter(({ field }) => !formData[field]);
    if (missingFields.length > 0) {
      setErrorMessage(`Please fill in the following required fields: ${missingFields.map(f => f.label).join(', ')}.`);
      return;
    }

    // Validate exact length requirements
    if (formData.fssaiNo.length !== 14 || !/^\d{14}$/.test(formData.fssaiNo)) {
      setErrorMessage('FSSAI License Number must be exactly 14 digits.');
      return;
    }

    if (formData.zipCode && formData.zipCode.length !== 6) {
      setErrorMessage('ZIP Code must be exactly 6 digits.');
      return;
    }

    if (formData.mobileNo && formData.mobileNo.length !== 10) {
      setErrorMessage('Mobile No must be exactly 10 digits.');
      return;
    }

    if (formData.phoneNo && formData.phoneNo.length !== 10) {
      setErrorMessage('Phone No must be exactly 10 digits.');
      return;
    }

    if (formData.panNo && (formData.panNo.length !== 10 || !/^[a-zA-Z0-9]{10}$/.test(formData.panNo))) {
      setErrorMessage('PAN No must be exactly 10 alphanumeric characters.');
      return;
    }

    if (formData.gstTinNo && (formData.gstTinNo.length !== 15 || !/^[a-zA-Z0-9]{15}$/.test(formData.gstTinNo))) {
      setErrorMessage('GST TIN No must be exactly 15 alphanumeric characters.');
      return;
    }

    setSaving(true);
    try {
      const encodedEmail = encodeEmailForPath(currentUser.email);

      if (supplier) {
        if (!supplier.id) {
          setErrorMessage('Invalid supplier data: ID is missing.');
          return;
        }
        const supplierRef = ref(database, `users/${encodedEmail}/suppliers/${supplier.id}`);
        await update(supplierRef, formData);
        setSuccessMessage('Supplier updated successfully!');
        alert('Supplier updated successfully!');
      } else {
        const suppliersRef = ref(database, `users/${encodedEmail}/suppliers`);
        const supplierData = {
          ...formData,
          createdAt: serverTimestamp(),
          createdBy: currentUser.email
        };
        await push(suppliersRef, supplierData);
        setSuccessMessage('Supplier added successfully!');
        alert('Supplier added successfully!');
        setFormData({
          visibility: true,
          supplierName: '',
          under: '',
          underDetails: '',
          openingBalance: '',
          dueDays: '',
          creditLimit: '',
          dlNo: '',
          dlValidUpto: '',
          fssaiNo: '',
          fssaiValidUpto: '',
          contactPerson: '',
          address: '',
          address2: '',
          address3: '',
          city: '',
          zipCode: '',
          state: 'Tamil Nadu',
          country: 'India',
          phoneNo: '',
          mobileNo: '',
          faxNo: '',
          email: '',
          enableLoyalty: false,
          cardType: '',
          cardNumber: '',
          loyaltyPoints: '',
          redeemPoints: '',
          cstNo: '',
          gstTinNo: '',
          panNo: '',
          printName: '',
          obAmount: '',
          advanceAmount: '',
          creditAmount: '',
          debit: '',
          balanceAmount: '',
          smsAlert: 'NO'
        });
      }

      if (onSuccess) onSuccess(formData);
    } catch (error) {
      console.error('Error saving data:', error);
      setErrorMessage(`Failed to save supplier data: ${error.message}`);
      alert(`Error: Failed to save supplier data: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      visibility: true,
      supplierName: '',
      under: '',
      underDetails: '',
      openingBalance: '',
      dueDays: '',
      creditLimit: '',
      dlNo: '',
      dlValidUpto: '',
      fssaiNo: '',
      fssaiValidUpto: '',
      contactPerson: '',
      address: '',
      address2: '',
      address3: '',
      city: '',
      zipCode: '',
      state: 'Tamil Nadu',
      country: 'India',
      phoneNo: '',
      mobileNo: '',
      faxNo: '',
      email: '',
      enableLoyalty: false,
      cardType: '',
      cardNumber: '',
      loyaltyPoints: '',
      redeemPoints: '',
      cstNo: '',
      gstTinNo: '',
      panNo: '',
      printName: '',
      obAmount: '',
      advanceAmount: '',
      creditAmount: '',
      debit: '',
      balanceAmount: '',
      smsAlert: 'NO'
    });
    setErrorMessage('');
    setSuccessMessage('');
    if (onCancel) onCancel();
  };

  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '2rem auto',
      padding: '2rem',
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      minHeight: '100vh',
    },
    formWrapper: {
      backgroundColor: '#ffffff',
      borderRadius: '20px',
      padding: '2.5rem',
      border: '1px solid #e2e8f0',
    },
    heading: {
      fontSize: '2rem',
      fontWeight: '700',
      marginBottom: '2rem',
      color: '#1f2937',
      textAlign: 'center',
    },
    messageSuccess: {
      padding: '1rem 1.5rem',
      marginBottom: '1.5rem',
      borderRadius: '12px',
      backgroundColor: '#d1fae5',
      color: '#065f46',
      border: '1px solid #a7f3d0',
      fontSize: '0.9rem',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    messageError: {
      padding: '1rem 1.5rem',
      marginBottom: '1.5rem',
      borderRadius: '12px',
      backgroundColor: '#fee2e2',
      color: '#b91c1c',
      border: '1px solid #fecaca',
      fontSize: '0.9rem',
      fontWeight: '500',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    loadingContainer: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#6b7280',
    },
    loadingSpinner: {
      width: '3rem',
      height: '3rem',
      border: '4px solid #e5e7eb',
      borderTop: '4px solid #2563eb',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      margin: '0 auto 1rem',
    },
    formLayout: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '2rem',
    },
    twoColumnLayout: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: '2rem',
    },
    section: {
      backgroundColor: '#fff',
      padding: '1.5rem',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    },
    sectionHeading: {
      fontSize: '1.25rem',
      fontWeight: '600',
      marginBottom: '1rem',
      color: '#1f2937',
    },
    inputGroup: {
      marginBottom: '1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    inputGroup1: {
      marginBottom: '1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      marginTop:'100px',
       marginLeft:'-338px'
    },
    label: {
      display: 'block',
      fontSize: '1rem',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '0.25rem',
    },
    requiredAsterisk: {
      color: '#ef4444',
      marginLeft: '0.25rem',
    },
    input: {
      display: 'block',
      width: '90%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
    },
    emailInput: {
      display: 'block',
      width: '95%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      backgroundColor: '#fff',
      transition: 'border-color 0.2s ease',
    },
    select: {
      display: 'block',
      width: '100%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      backgroundColor: '#fff',
      cursor: 'pointer',
      appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
      backgroundPosition: 'right 0.5rem center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '1em 1em',
      paddingRight: '2rem',
    },
    checkboxContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    checkbox: {
      width: '1rem',
      height: '1rem',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      cursor: 'pointer',
    },
    gridTwoColumns: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '1rem',
    },
    buttonContainer: {
      marginTop: '2rem',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      paddingTop: '1rem',
      borderTop: '1px solid #e2e8f0',
    },
    button: {
      padding: '0.5rem 1.5rem',
      borderRadius: '6px',
      fontWeight: '500',
      fontSize: '0.875rem',
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s ease',
    },
    submitButton: {
      backgroundColor: '#10b981',
      color: 'white',
    },
    cancelButton: {
      backgroundColor: '#ef4444',
      color: 'white',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.formWrapper}>
        <h1 style={styles.heading}>👤 {supplier ? 'Edit Supplier' : 'Add New Supplier'}</h1>

        {successMessage && (
          <div style={styles.messageSuccess}>
            <span>✅</span>
            {successMessage}
          </div>
        )}

        {errorMessage && (
          <div style={styles.messageError}>
            <span>❌</span>
            {errorMessage}
          </div>
        )}

        {loading ? (
          <div style={styles.loadingContainer}>
            <div style={styles.loadingSpinner}></div>
            <p>Loading form data...</p>
          </div>
        ) : (
          <form onSubmit={handleSave}>
            <div style={styles.formLayout}>
              <div style={styles.twoColumnLayout}>
                <div style={styles.section}>
                  <h2 style={styles.sectionHeading}>📋 General Information</h2>
                  <div style={styles.inputGroup}>
                    <div style={styles.checkboxContainer}>
                      <input
                        type="checkbox"
                        name="visibility"
                        checked={formData.visibility}
                        onChange={handleChange}
                        style={styles.checkbox}
                      />
                      <label style={styles.label}>Visibility</label>
                    </div>
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>
                      Supplier Name<span style={styles.requiredAsterisk}>*</span>
                    </label>
                    <input
                      type="text"
                      name="supplierName"
                      value={formData.supplierName}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="Enter supplier name (letters, spaces, hyphens)"
                      required
                      title="Only letters, spaces, and hyphens allowed"
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>
                      Under<span style={styles.requiredAsterisk}>*</span>
                    </label>
                    <select
                      name="under"
                      value={formData.under}
                      onChange={handleChange}
                      style={styles.select}
                      required
                    >
                      <option value="">Select Group</option>
                      <option value="CR">CR</option>
                      <option value="DB">DB</option>
                    </select>
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Under Details</label>
                    <input
                      type="text"
                      name="underDetails"
                      value={formData.underDetails}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="Enter under details"
                    />
                  </div>
                  <div style={styles.gridTwoColumns}>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>
                        Opening Balance<span style={styles.requiredAsterisk}>*</span>
                      </label>
                      <input
                        type="number"
                        name="openingBalance"
                        value={formData.openingBalance}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="0.00"
                        required
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Due Days</label>
                      <input
                        type="number"
                        name="dueDays"
                        value={formData.dueDays}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="0"
                        min="0"
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Credit Limit</label>
                      <input
                        type="number"
                        name="creditLimit"
                        value={formData.creditLimit}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <div style={{ ...styles.section, marginBottom: '2rem' }}>
                    <h2 style={styles.sectionHeading}>🪪 ID Proof</h2>
                    <div style={styles.gridTwoColumns}>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>
                          DL No<span style={styles.requiredAsterisk}>*</span>
                        </label>
                        <input
                          type="text"
                          name="dlNo"
                          value={formData.dlNo}
                          onChange={handleChange}
                          style={styles.input}
                          placeholder="Enter DL No (letters, numbers, spaces)"
                          required
                          title="Only letters, numbers, and spaces allowed"
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>
                          Valid Upto<span style={styles.requiredAsterisk}>*</span>
                        </label>
                        <input
                          type="date"
                          name="dlValidUpto"
                          value={formData.dlValidUpto}
                          onChange={handleChange}
                          style={styles.input}
                          required
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>
                          FSSAI No<span style={styles.requiredAsterisk}>*</span>
                        </label>
                        <input
                          type="text"
                          name="fssaiNo"
                          value={formData.fssaiNo}
                          onChange={handleChange}
                          style={styles.input}
                          placeholder="Enter 14-digit FSSAI number"
                          required
                          maxLength={14}
                          pattern="\d{14}"
                          title="FSSAI No must be exactly 14 digits"
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>
                          Valid Upto<span style={styles.requiredAsterisk}>*</span>
                        </label>
                        <input
                          type="date"
                          name="fssaiValidUpto"
                          value={formData.fssaiValidUpto}
                          onChange={handleChange}
                          style={styles.input}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div style={styles.section}>
                    <h2 style={styles.sectionHeading}>📞 Contact Information</h2>
                    <div style={styles.gridTwoColumns}>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Contact Person</label>
                        <input
                          type="text"
                          name="contactPerson"
                          value={formData.contactPerson}
                          onChange={handleChange}
                          style={styles.input}
                          placeholder="Enter contact person (letters, spaces)"
                          title="Only letters and spaces allowed"
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Address</label>
                        <input
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          style={styles.input}
                          placeholder="Enter address (letters, spaces)"
                          title="Only letters and spaces allowed"
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Address 2</label>
                        <input
                          type="text"
                          name="address2"
                          value={formData.address2}
                          onChange={handleChange}
                          style={styles.input}
                          placeholder="Enter address 2 (letters, spaces)"
                          title="Only letters and spaces allowed"
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Address 3</label>
                        <input
                          type="text"
                          name="address3"
                          value={formData.address3}
                          onChange={handleChange}
                          style={styles.input}
                          placeholder="Enter address 3 (letters, spaces)"
                          title="Only letters and spaces allowed"
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>City</label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          style={styles.input}
                          placeholder="Enter city (letters, spaces)"
                          title="Only letters and spaces allowed"
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>ZIP Code</label>
                        <input
                          type="text"
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={handleChange}
                          style={styles.input}
                          placeholder="Enter 6-digit ZIP code"
                          maxLength={6}
                          pattern="\d{6}"
                          title="ZIP Code must be exactly 6 digits"
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Country</label>
                        <select
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          style={styles.select}
                          disabled={loading || countries.length === 0}
                        >
                          <option value="">Select Country</option>
                          {countries.map((country) => (
                            <option key={country.code} value={country.name}>
                              {country.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>State</label>
                        <select
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          style={styles.select}
                          disabled={loading || !formData.country}
                        >
                          <option value="">Select State</option>
                          {states.map((state) => (
                            <option key={state.name} value={state.name}>
                              {state.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Phone No</label>
                        <input
                          type="text"
                          name="phoneNo"
                          value={formData.phoneNo}
                          onChange={handleChange}
                          style={styles.input}
                          placeholder="Enter phone number"
                          maxLength={10}
                          pattern="\d{10}"
                          title="Phone No must be exactly 10 digits"
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Mobile No</label>
                        <input
                          type="text"
                          name="mobileNo"
                          value={formData.mobileNo}
                          onChange={handleChange}
                          style={styles.input}
                          placeholder="Enter mobile number"
                          maxLength={10}
                          pattern="\d{10}"
                          title="Mobile No must be exactly 10 digits"
                        />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>Fax No</label>
                        <input
                          type="text"
                          name="faxNo"
                          value={formData.faxNo}
                          onChange={handleChange}
                          style={styles.input}
                          placeholder="Enter fax number (letters, numbers, symbols)"
                          title="Letters, numbers, and special symbols allowed"
                        />
                      </div>
                      <div style={styles.inputGroup1}>
                        <label style={styles.label}>E-Mail ID</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          style={ {display: 'block',
                            width: '200px',
                            padding: '0.5rem 0.75rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '0.875rem',
                            backgroundColor: '#fff',
                            transition: 'border-color 0.2s ease',}}
                          placeholder="Enter email address"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div style={styles.section}>
                <h2 style={styles.sectionHeading}>🎁 Loyalty Details</h2>
                <div style={styles.inputGroup}>
                  <div style={styles.checkboxContainer}>
                    <input
                      type="checkbox"
                      name="enableLoyalty"
                      checked={formData.enableLoyalty}
                      onChange={handleChange}
                      style={styles.checkbox}
                    />
                    <label style={styles.label}>
                      Enable Loyalty{formData.enableLoyalty && <span style={styles.requiredAsterisk}>*</span>}
                    </label>
                  </div>
                </div>
                {formData.enableLoyalty && (
                  <div style={styles.gridTwoColumns}>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>
                        Card Type<span style={styles.requiredAsterisk}>*</span>
                      </label>
                      <select
                        name="cardType"
                        value={formData.cardType}
                        onChange={handleChange}
                        style={styles.select}
                        required={formData.enableLoyalty}
                      >
                        <option value="">SELECT</option>
                        <option value="Gold">Gold</option>
                        <option value="Silver">Silver</option>
                      </select>
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>
                        Card Number<span style={styles.requiredAsterisk}>*</span>
                      </label>
                      <input
                        type="text"
                        name="cardNumber"
                        value={formData.cardNumber}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="Enter card number"
                        required={formData.enableLoyalty}
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>
                        Loyalty Points<span style={styles.requiredAsterisk}>*</span>
                      </label>
                      <input
                        type="number"
                        name="loyaltyPoints"
                        value={formData.loyaltyPoints}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="0"
                        min="0"
                        required={formData.enableLoyalty}
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>
                        Redeem Points<span style={styles.requiredAsterisk}>*</span>
                      </label>
                      <input
                        type="number"
                        name="redeemPoints"
                        value={formData.redeemPoints}
                        onChange={handleChange}
                        style={styles.input}
                        placeholder="0"
                        min="0"
                        required={formData.enableLoyalty}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div style={styles.section}>
                <h2 style={styles.sectionHeading}>🏢 Company Information</h2>
                <div style={styles.gridTwoColumns}>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>CST No</label>
                    <input
                      type="text"
                      name="cstNo"
                      value={formData.cstNo}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="Enter CST No (letters, numbers, symbols)"
                      title="Letters, numbers, and special symbols allowed"
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>GST TIN No</label>
                    <input
                      type="text"
                      name="gstTinNo"
                      value={formData.gstTinNo}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="Enter 15-digit GST TIN No"
                      maxLength={15}
                      pattern="[a-zA-Z0-9]{15}"
                      title="GST TIN No must be exactly 15 alphanumeric characters"
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>PAN No</label>
                    <input
                      type="text"
                      name="panNo"
                      value={formData.panNo}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="Enter 10-digit PAN No"
                      maxLength={10}
                      pattern="[a-zA-Z0-9]{10}"
                      title="PAN No must be exactly 10 alphanumeric characters"
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Print Name</label>
                    <input
                      type="text"
                      name="printName"
                      value={formData.printName}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="Enter print name (letters, spaces)"
                      title="Only letters and spaces allowed"
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>OB Amount</label>
                    <input
                      type="number"
                      name="obAmount"
                      value={formData.obAmount}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Advance Amount</label>
                    <input
                      type="number"
                      name="advanceAmount"
                      value={formData.advanceAmount}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Credit Amount</label>
                    <input
                      type="number"
                      name="creditAmount"
                      value={formData.creditAmount}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Debit</label>
                    <input
                      type="number"
                      name="debit"
                      value={formData.debit}
                      onChange={handleChange}
                      style={styles.input}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Balance Amount</label>
                    <input
                      type="number"
                      name="balanceAmount"
                      value={formData.balanceAmount}
                      style={styles.input}
                      placeholder="0.00"
                      readOnly
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>SMS Alert</label>
                    <select
                      name="smsAlert"
                      value={formData.smsAlert}
                      onChange={handleChange}
                      style={styles.select}
                    >
                      <option value="YES">YES</option>
                      <option value="NO">NO</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.buttonContainer}>
              <button
                type="button"
                onClick={handleCancel}
                style={{ ...styles.button, ...styles.cancelButton }}
                disabled={saving || loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{ ...styles.button, ...styles.submitButton }}
                disabled={saving || loading}
              >
                {saving ? '⏳ Saving...' : supplier ? 'Update Supplier' : 'Add Supplier'}
              </button>
            </div>
          </form>
        )}
      </div>

      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        @media (max-width: 768px) {
          .twoColumnLayout {
            grid-template-columns: 1fr;
          }
          .gridTwoColumns {
            grid-template-columns: 1fr;
          }
          .buttonContainer {
            flex-direction: column;
          }
          .button {
            width: 100%;
            justify-content: center;
          }
        }
        input:focus,
        select:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
        }
        input:hover,
        select:hover {
          border-color: #a1a1aa;
        }
        button:hover:not(:disabled) {
          filter: brightness(90%);
        }
      `}</style>
    </div>
  );
};

SupplierForm.propTypes = {
  supplier: PropTypes.shape({
    id: PropTypes.string,
    visibility: PropTypes.bool,
    supplierName: PropTypes.string,
    under: PropTypes.string,
    underDetails: PropTypes.string,
    openingBalance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dueDays: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    creditLimit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dlNo: PropTypes.string,
    dlValidUpto: PropTypes.string,
    fssaiNo: PropTypes.string,
    fssaiValidUpto: PropTypes.string,
    contactPerson: PropTypes.string,
    address: PropTypes.string,
    address2: PropTypes.string,
    address3: PropTypes.string,
    city: PropTypes.string,
    zipCode: PropTypes.string,
    state: PropTypes.string,
    country: PropTypes.string,
    phoneNo: PropTypes.string,
    mobileNo: PropTypes.string,
    faxNo: PropTypes.string,
    email: PropTypes.string,
    enableLoyalty: PropTypes.bool,
    cardType: PropTypes.string,
    cardNumber: PropTypes.string,
    loyaltyPoints: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    redeemPoints: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    cstNo: PropTypes.string,
    gstTinNo: PropTypes.string,
    panNo: PropTypes.string,
    printName: PropTypes.string,
    obAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    advanceAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    creditAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    debit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    balanceAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    smsAlert: PropTypes.string
  }),
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func
};

export default SupplierForm;